import type { KeyboardEventHandler } from 'react';
import { useMemo } from 'react';
import type { GroupBase, StylesConfig } from 'react-select';
import SelectAsync from 'react-select';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { RepSearchResult } from '@trinity/chat';
import {
  buildSelectStyles,
  buildSelectTheme,
} from '@trinity/chat-components/components/Chat.Filters/Presentation.js';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useDebounceValue } from '@trinity/chat-components/hooks/useDebounceValue';

export type SelectRepProps = {
  disabled?: boolean;
  inputValue: string;
  selectedRep: RepSearchResult | null;
  onInputChange: (value: string) => void;
  onSelection: (rep: RepSearchResult) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  queryFn: (params: { name: string; brandIdentifier: string | null }) => Promise<RepSearchResult[]>;
};

export function SelectRep({
  disabled,
  inputValue,
  selectedRep,
  onInputChange,
  onSelection,
  onKeyDown,
  queryFn,
}: SelectRepProps) {
  const theme = useTheme();
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const debouncedInputValue = useDebounceValue(inputValue, 250);
  const trimmedDebouncedInputValue = debouncedInputValue.trim();

  const searchQuery = useQuery({
    queryKey: ['chat', 'rep-search-autocomplete', trimmedDebouncedInputValue, brandIdentifier],
    queryFn: () => queryFn({ name: trimmedDebouncedInputValue, brandIdentifier }),
    enabled: trimmedDebouncedInputValue.length > 0 && !disabled,
    placeholderData: [] as RepSearchResult[],
  });

  const options = searchQuery.data ?? [];

  const selectStyles = useMemo(
    () => buildSelectStyles(theme) as StylesConfig<RepSearchResult, false, GroupBase<RepSearchResult>>,
    [theme]
  );
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  return (
    <SelectWrap>
      <SelectAsync<RepSearchResult>
        menuPortalTarget={document.body}
        isDisabled={disabled}
        onKeyDown={options.length > 0 ? undefined : onKeyDown}
        onChange={(option) => {
          if (option) {
            onSelection(option);
          }
        }}
        inputValue={inputValue}
        value={selectedRep}
        isLoading={searchQuery.isFetching}
        options={options}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        noOptionsMessage={() => null}
        onInputChange={(value, meta) => {
          if (meta.action === 'input-change') {
            onInputChange(value);
          }
        }}
        placeholder=""
        styles={selectStyles}
        filterOption={() => true}
        theme={selectTheme}
      />
    </SelectWrap>
  );
}

const SelectWrap = styled.div({
  flex: 1,
  maxWidth: 280,
});
